import { buyColor } from "../../assets/colors";
import React, { useContext, useMemo } from 'react'
import { Box, Checkbox, FormGroup, FormControlLabel, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { AppContext } from '../../App'
import { selectChartConfig } from '../../selectors/chart'
import { HollowCandleSeriesOptions } from '../chartV2/chart.typings'
import ColorSelector from '../colorSelector/colorSelector'
import tinycolor from 'tinycolor2'
import CommonChartSettingsTabs from './commonChartSettingsTabs'
import { chartSettingsColorRowSx, chartSettingsFormGroupSx, chartSettingsSectionTitleSx, chartSettingsCheckboxColorLabelSx } from './chartSettingsLayout'

type Props = {
    viewKey: string
}

const HollowCandlestickSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    const upColorHollow = useMemo(() => {
        let color = buyColor
        if (primarySeriesGroupId) {
            console.log(chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series, 'primarySeriesGroupId')
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColorHollow ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const downColorHollow = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColorHollow ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderUpColorHollow = useMemo(() => {
        let color = buyColor

        if (primarySeriesGroupId) {
            console.log(chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0], 'primarySeriesGroupId')
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderUpColorHollow ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderDownColorHollow = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderDownColorHollow ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickUpColorHollow = useMemo(() => {
        let color = buyColor
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickUpColorHollow ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickDownColorHollow = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickDownColorHollow ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const visible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.visible ?? true
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    const borderVisibleHollow = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.borderVisibleHollow ?? false
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const wickVisibleHollow = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.wickVisibleHollow ?? true
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof HollowCandleSeriesOptions) => {
        if (chart) {
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions({
                    [type]: newColor
                } as Partial<HollowCandleSeriesOptions>)
            }
        }
    }
    const handleBooleanChange = (value: boolean, type: keyof HollowCandleSeriesOptions) => {
        if (chart) {
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions({
                    [type]: value
                } as Partial<HollowCandleSeriesOptions>)
            }
        }
    }

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                HOLLOW CANDLES
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        sx={chartSettingsCheckboxColorLabelSx}
                        control={<Checkbox checked={visible} onChange={(e) => handleBooleanChange(e.target.checked, 'visible' as any)} />}
                        label="Body"
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={upColorHollow.toHexString()}
                        opacity={upColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'upColorHollow')
                        }}
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={downColorHollow.toHexString()}
                        opacity={downColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'downColorHollow')
                        }}
                    />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        sx={chartSettingsCheckboxColorLabelSx}
                        control={<Checkbox checked={borderVisibleHollow} onChange={(e) => handleBooleanChange(e.target.checked, 'borderVisibleHollow' as any)} />}
                        label="Border"
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={borderUpColorHollow.toHexString()}
                        opacity={borderUpColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'borderUpColorHollow')
                        }}
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={borderDownColorHollow.toHexString()}
                        opacity={borderDownColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'borderDownColorHollow')
                        }}
                    />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        sx={chartSettingsCheckboxColorLabelSx}
                        control={<Checkbox checked={wickVisibleHollow} onChange={(e) => handleBooleanChange(e.target.checked, 'wickVisibleHollow' as any)} />}
                        label="Wick"
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={wickUpColorHollow.toHexString()}
                        opacity={wickUpColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'wickUpColorHollow')
                        }}
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={wickDownColorHollow.toHexString()}
                        opacity={wickDownColorHollow.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'wickDownColorHollow')
                        }}
                    />
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default HollowCandlestickSettings
